import {ee} from "../../services/earth-engine";
import { aoiGeometry } from '../../common/aoi';


/**
 * The transect measurement and the classifier live in one place now; see
 * {@link module:LAND_COVER_TRANSECTS}. Re-exported rather than imported by the consumers so every
 * existing import site keeps reading them from this module.
 */
export { extractShoreLineTurf, landCoversIntersectionsTurf, trainAndClassify } from '../landCoverTransects';

/**
 * Satellite/index helpers tagged `module:SHARED` are cross-module infrastructure that happens to
 * live under algorithms/csqueeze. EUSCoast re-exports them instead of duplicating, so the saga and
 * the components have a single import surface (`algorithms/euscoast`) and there is only ever one
 * implementation of each.
 */
export {
  applyScaleFactors,
  applyNdviBand,
  applyMndwiBand,
  applyUiBand,
  maskCloudLandsat,
  maskCloudSentinel,
} from "../csqueeze";


/**
 * MapBiomas Brazil land use / land cover, Collection 10 (1985-2024).
 * Published as an ImageCollection: one image per year, single `classification` band, with `year`
 * and `collection_id` properties. Changing MAPBIOMAS_COLLECTION is enough to move to a newer
 * collection, as long as it is served from the same asset.
 */
const MAPBIOMAS_ASSET = 'projects/mapbiomas-public/assets/brazil/lulc/v1';
const MAPBIOMAS_COLLECTION = 10;

/**
 * Urban growth thresholds, in percent, applied to (projectedUrban / observedUrban - 1) * 100.
 * Tune these two numbers to move the category boundaries; nothing else needs to change.
 *   growth <= URBAN_GROWTH_STABLE_MAX     -> stable   (green)
 *   growth <  URBAN_GROWTH_ADVANCED_MIN   -> moderate (yellow)
 *   growth >= URBAN_GROWTH_ADVANCED_MIN   -> advanced (red)
 */
export const URBAN_GROWTH_STABLE_MAX = 0;
export const URBAN_GROWTH_ADVANCED_MIN = 100;

/**
 * Column of the human intervention (urban) class inside a `rows` entry.
 * rows = [year, human, vegetation, mangrove, water, beach] - index 0 holds the year.
 */
export const URBAN_COLUMN = 1;

export const URBAN_GROWTH_COLORS = {
  advanced: 'red',
  moderate: 'yellow',
  stable: 'green',
  noUrban: 'gray',
};






/**
 * @function getMapBiomasClassificationsList
 * @description Lists every year available in the MapBiomas Collection 10 land-use/land-cover
 * asset. Collection 10 is published as an ImageCollection (one image per year, single
 * `classification` band) rather than as the one-band-per-year image Collection 9 used.
 * **Called by:
 * @see {LandCover}
 * @memberof module:EUSCOAST
 * @returns {Array<String>} Available years, as strings.
 */
export const getMapBiomasClassificationsList = () => {
  return ee.ImageCollection(MAPBIOMAS_ASSET)
    .filter(ee.Filter.eq('collection_id', MAPBIOMAS_COLLECTION))
    .aggregate_array('year')
    .distinct()
    .sort()
    .getInfo()
    .map(String);
}

/**
 * @function getMapBiomasClassificationYear
 * @description Clips a single year's MapBiomas Collection 10 classification to the AOI. Unlike
 * CSqueeze there is no elevation mask here: EUSCoast measures urban growth at any elevation
 * inside the AOI, so the analysis reach is set by the transect length alone.
 * @param {Array} AOI - Area of interest polygon coordinates.
 * @param {Number|String} year - Classification year to select.
 * **Called by:
 * @see {LandCover}
 * @memberof module:EUSCOAST
 * @returns {ee.Image} The classification image for the given year, clipped to the AOI.
 */
export const getMapBiomasClassificationYear = (AOI, year) => {
  const requested = parseInt(year, 10);
  if (!Number.isFinite(requested)) {
    throw new Error(`EUSCoast: "${year}" is not a MapBiomas classification year.`);
  }
  return ee.Image(
    ee.ImageCollection(MAPBIOMAS_ASSET)
      .filter(ee.Filter.eq('collection_id', MAPBIOMAS_COLLECTION))
      .filter(ee.Filter.eq('year', requested))
      .first(),
  )
    .select('classification')
    .clip(aoiGeometry(AOI));
}

/**
 * @function getLandCoverLabelMapBiomas
 * @description Maps a raw MapBiomas classification code to EUSCoast's 5-class land-cover label
 * (0 mangrove, 1 vegetation, 2 water, 3 human intervention, 4 beach/dune/sand spot), defaulting
 * to human intervention (3) for unrecognized codes.
 * @param {Number} value - Raw MapBiomas classification code.
 * **Called by:
 * @see {handleTransformClassToPolygons}
 * @memberof module:EUSCOAST
 * @returns {Number} EUSCoast land-cover label (0-4).
 */
export function getLandCoverLabelMapBiomas(value) {
  let landcoverstypes = {
    mangrove: {
      label: 0,
      types: [5]
    },
    vegetation: {
      label: 1,
      types: [1, 3, 4, 5, 49, 10, 11, 12, 32, 29, 50, 13]
    },
    water: {
      label: 2,
      types: [26, 33]
    },
    human: {
      label: 3,
      types: [14, 15, 18, 19, 39, 20, 40, 62, 41, 36, 46, 47, 48, 9, 21, 22, 24, 30, 25, 31, 27, 75]
    },
    beach: {
      label: 4,
      types: [23]
    },
  }

  for (const type of Object.entries(landcoverstypes)) {
    if (type[1].types.includes(value)) {
      return type[1].label;
    }
  }

  return 3;
}



/**
 * @function classifyUrbanGrowth
 * @description Classifies one transect's urban growth from the projection table. The percentage is
 * (projectedUrban - observedUrban) / observedUrban * 100, i.e. the potential expansion over the
 * chosen forecast horizon - the direct counterpart of CSqueeze's squeeze percentage.
 *
 * Kept in one place on purpose: both the map polyline color (result.jsx) and the panel message
 * (_TransectResult.jsx) read it, so the thresholds can never drift apart between the two.
 * @param {Array<Array>} rows - Table rows; rows[2] is the last observed year, rows[3] the forecast.
 * A table without both of those has no verdict to give and is answered as `noUrban` rather than
 * throwing: the panel reads this while the projection table is still being published.
 * **Called by:
 * @see {ProcessingPage}
 * @see {TransectResult}
 * @memberof module:EUSCOAST
 * @returns {{category: String, growth: Number, color: String}} Category key, growth percentage
 * (0 when undefined) and the polyline color for that category.
 */
export const classifyUrbanGrowth = (rows) => {
  if (!rows?.[2] || !rows?.[3]) {
    return { category: 'noUrban', growth: 0, color: URBAN_GROWTH_COLORS.noUrban };
  }
  const observedUrban = rows[2][URBAN_COLUMN];
  const projectedUrban = rows[3][URBAN_COLUMN];

  // No urban area in the final observed year: the variation is undefined, not zero.
  if (!(observedUrban > 0)) {
    return { category: 'noUrban', growth: 0, color: URBAN_GROWTH_COLORS.noUrban };
  }

  const growth = (projectedUrban / observedUrban - 1) * 100;

  if (growth >= URBAN_GROWTH_ADVANCED_MIN) {
    return { category: 'advancedGrowth', growth, color: URBAN_GROWTH_COLORS.advanced };
  }

  if (growth > URBAN_GROWTH_STABLE_MAX) {
    return { category: 'moderateGrowth', growth, color: URBAN_GROWTH_COLORS.moderate };
  }

  return { category: 'stable', growth, color: URBAN_GROWTH_COLORS.stable };
}
